import { Children, forwardRef, isValidElement, type ButtonHTMLAttributes, type ChangeEvent, type InputHTMLAttributes, type ReactElement, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { getDefaultButtons, type TButtonVariant } from "@ui/button/Button";
import { createCSSFromObject } from "@ui/box/Box";
import Input, { InputBoxStyle, InputEl } from "@ui/input/Input";
import { Select } from "@ui/select/Select";
import styled from "styled-components";
import type { ITheme } from "@ui/theme/GlobalTheme";

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant: TButtonVariant; size?: "small" };

const SharedButton = styled.button<{ $variant: TButtonVariant; $size?: "small" }>`
	box-sizing: border-box;
	appearance: none;
	border: 0;
	font-family: inherit;
	line-height: inherit;
	${({ theme, $variant }) => {
		const uiTheme = theme as ITheme;
		const colors = uiTheme.colors;
		const styles = uiTheme.buttons?.[$variant] ?? getDefaultButtons[$variant];
		const resolve = (value: Record<string, unknown>): Record<string, unknown> => Object.fromEntries(Object.entries(value).map(([key, item]) => {
			if (item && typeof item === "object" && !Array.isArray(item)) return [key, resolve(item as Record<string, unknown>)];
			return [key, typeof item === "string" && item in colors ? colors[item as keyof typeof colors] : item];
		}));
		return createCSSFromObject(resolve(styles));
	}}
	${({ $size }) => $size === "small" && "height: 36px; padding-block: 0;"}
	&:disabled { opacity: .5; cursor: default; }
`;

// Apply the existing UI theme to one element so page layout styles still work.
export const AdminButton = forwardRef<HTMLButtonElement, ButtonProps>(({ children, className, variant, size, type = "button", disabled, onClick, style, ...props }, ref) =>
	<SharedButton {...props} ref={ref} className={className} type={type} disabled={disabled} onClick={onClick} style={style} $variant={variant} $size={size}>{children}</SharedButton>
);

const InputFrame = styled.div`
	${({ theme }) => {
		const style = InputBoxStyle(false);
		const colors = (theme as ITheme).colors;
		return `border: 1px solid ${colors.inputBorderPrimary}; border-radius: ${style.borderRadius}; background: ${colors.inputBackgroundPrimary};`;
	}}
	min-width: 0;
	&:focus-within { border-color: #1a5bff; }
	&:has(input:disabled) { background: rgba(0, 0, 0, .07); border-color: rgba(0, 0, 0, .1); }
`;

type InputProps = InputHTMLAttributes<HTMLInputElement>;
export const AdminInput = forwardRef<HTMLInputElement, InputProps>(({ value, onChange, type = "text", disabled, readOnly, placeholder, autoFocus, required, min, max, step, pattern, inputMode, autoComplete, ...props }, ref) => <InputFrame>
	<InputEl {...props as object} ref={ref} $size="small" type={type} disabled={disabled} readOnly={readOnly} placeholder={placeholder} autoFocus={autoFocus} required={required} min={min} max={max} step={step} pattern={pattern} inputMode={inputMode} autoComplete={autoComplete} value={value} onChange={onChange as never} />
</InputFrame>);

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;
export const AdminTextarea = ({ value, onChange, disabled, readOnly }: TextareaProps) => <Input
	name="value" type="textarea" size="small" disabled={disabled} readOnly={readOnly} data={{ value: String(value ?? "") }} onChange={next => onChange?.({ target: { value: next } } as ChangeEvent<HTMLTextAreaElement>)}
/>;

type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, "onChange"> & { onChange?: (event: ChangeEvent<HTMLSelectElement>) => void; children: ReactNode };
export const AdminSelect = ({ value, onChange, children, disabled }: SelectProps) => {
	const options = Children.toArray(children).filter(isValidElement) as ReactElement<{ value: string | number; children: ReactNode }>[];
	const values = options.map(option => ({ name: String(option.props.children), value: String(option.props.value) }));
	return <Select<any> size="small" disabled={disabled} value={values.find(option => option.value === String(value ?? "")) ?? null} values={values} onChange={next => onChange?.({ target: { value: String(next) } } as ChangeEvent<HTMLSelectElement>)} />;
};
